import { useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebsocket } from "@finitless/shared/contexts";
import { useBrand } from "@finitless/shared/contexts";
import { applyConversationMessageToCache } from "@finitless/shared/lib";
import { useSmartPagination } from "./useSmartPagination";

/**
 * Hook for handling real-time conversation updates via websockets
 * Integrates with React Query for cache updates
 */
export const useConversationSocketUpdates = (options = {}) => {
  const queryClient = useQueryClient();
  const { isConnected, registerEventListener, unregisterEventListener } =
    useWebsocket();
  const { selectedBrand } = useBrand();
  const [listenersSetup, setListenersSetup] = useState(false);
  const listenersRegistered = useRef(false);
  const hookId = useRef(
    `useConversationSocketUpdates-${Date.now()}-${Math.random()}`,
  );

  // Smart pagination integration
  const smartPagination = useSmartPagination(["conversations"], {
    itemsPerPage: options.itemsPerPage || 10,
    onNewItemAdded: options?.onNewItemAdded || (() => {}),
    onItemUpdated: options?.onItemUpdated || (() => {}),
    onItemRemoved: options?.onItemRemoved || (() => {}),
  });

  /**
   * Update conversation in cache
   */
  const updateConversationInCache = useCallback(
    (updatedConversation) => {
      // Update all conversation queries that might contain this conversation
      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) return oldData;

        const updatedConversations = oldData.conversations.map(
          (conversation) =>
            conversation.id === updatedConversation.id
              ? { ...conversation, ...updatedConversation }
              : conversation,
        );

        return {
          ...oldData,
          conversations: updatedConversations,
        };
      });

      // Also update individual conversation queries if they exist
      queryClient.setQueryData(
        ["conversation", updatedConversation.id],
        updatedConversation,
      );
    },
    [queryClient],
  );

  /**
   * Add new conversation to cache
   */
  const addConversationToCache = useCallback(
    (conversationData) => {
      if (conversationData?.id == null) return;
      // Only process conversations for the current brand
      if (
        selectedBrand?.id &&
        conversationData.business_id !== selectedBrand.id
      ) {
        return;
      }

      // The same entity may already be present in another cached page/filter.
      // A replay must not increment totals in projections that omit its row.
      const alreadyCached = queryClient
        .getQueriesData({ queryKey: ["conversations"] })
        .some(([, data]) =>
          data?.conversations?.some(
            (item) => String(item.id) === String(conversationData.id),
          ),
        );
      if (alreadyCached) return;
      let added = false;
      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) {
          added = true;
          return {
            conversations: [conversationData],
            pagination: { total: 1, page: 1, page_size: 10 },
          };
        }

        // A repeated creation event must not duplicate rows or pagination.
        if (
          oldData.conversations.some((item) => item.id === conversationData.id)
        ) {
          return oldData;
        }

        added = true;
        // Add new conversation to the beginning of the list
        const updatedConversations = [
          conversationData,
          ...oldData.conversations,
        ];

        return {
          ...oldData,
          conversations: updatedConversations,
          pagination: {
            ...oldData.pagination,
            total:
              (oldData.pagination?.total ?? oldData.conversations.length) + 1,
          },
        };
      });

      if (added) smartPagination?.handleItemAdded?.(conversationData);
    },
    [selectedBrand?.id, queryClient, smartPagination],
  );

  /**
   * Handle new conversation
   */
  const handleNewConversation = useCallback(
    (conversationData) => {
      if (conversationData?.id == null) return;
      // Only process conversations for the current brand
      if (
        selectedBrand?.id &&
        conversationData.business_id !== selectedBrand.id
      ) {
        return;
      }

      addConversationToCache(conversationData);
    },
    [selectedBrand?.id, addConversationToCache],
  );

  /**
   * Handle conversation updates
   */
  const handleConversationUpdate = useCallback(
    (conversationData) => {
      if (conversationData?.id == null) return;
      // Only process conversations for the current brand
      if (
        selectedBrand?.id &&
        conversationData.business_id !== selectedBrand.id
      ) {
        return;
      }

      updateConversationInCache(conversationData);
      smartPagination.handleItemUpdated(conversationData);
    },
    [selectedBrand?.id, updateConversationInCache, smartPagination],
  );

  /**
   * Handle new conversation messages
   */
  const handleNewConversationMessage = useCallback(
    (messageData) => {
      const cachedConversation = queryClient
        .getQueriesData({ queryKey: ["conversations"] })
        .flatMap(([, data]) => data?.conversations || [])
        .find((item) => item.id === messageData?.conversation_id);
      const businessId =
        messageData?.business_id ?? cachedConversation?.business_id;
      if (
        selectedBrand?.id &&
        businessId != null &&
        businessId !== selectedBrand.id
      ) {
        return;
      }
      applyConversationMessageToCache(queryClient, messageData);
    },
    [queryClient, selectedBrand?.id],
  );

  // Use refs to maintain stable references
  const handleNewConversationRef = useRef(handleNewConversation);
  const handleConversationUpdateRef = useRef(handleConversationUpdate);
  const handleNewConversationMessageRef = useRef(handleNewConversationMessage);

  // Update refs when handlers change
  useEffect(() => {
    handleNewConversationRef.current = handleNewConversation;
    handleConversationUpdateRef.current = handleConversationUpdate;
    handleNewConversationMessageRef.current = handleNewConversationMessage;
  }, [
    handleNewConversation,
    handleConversationUpdate,
    handleNewConversationMessage,
  ]);

  /**
   * Setup socket listeners using centralized event system
   */
  const setupSocketListeners = useCallback(() => {
    if (!isConnected || listenersRegistered.current) return;
    registerEventListener(
      "conversation.created",
      (data) => handleNewConversationRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "conversation.updated",
      (data) => handleConversationUpdateRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "conversation.message.created",
      (data) => handleNewConversationMessageRef.current(data),
      hookId.current,
    );
    listenersRegistered.current = true;
    setListenersSetup(true);
  }, [isConnected, registerEventListener]);

  const cleanupSocketListeners = useCallback(() => {
    if (!listenersRegistered.current) return;
    unregisterEventListener("conversation.created", hookId.current);
    unregisterEventListener("conversation.updated", hookId.current);
    unregisterEventListener("conversation.message.created", hookId.current);
    listenersRegistered.current = false;
    setListenersSetup(false);
  }, [unregisterEventListener]);

  useEffect(() => {
    setupSocketListeners();
    return cleanupSocketListeners;
  }, [setupSocketListeners, cleanupSocketListeners]);

  return {
    isConnected,
    listenersSetup,
    // Manual control methods
    setupSocketListeners,
    cleanupSocketListeners,
    // Cache update methods (for manual use)
    updateConversationInCache,
    addConversationToCache,
    // Smart pagination integration
    smartPagination,
  };
};

export default useConversationSocketUpdates;
